import classNames from 'clsx';
import { isNil } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PrizeCurrency } from '@swf/casino';
import CasinoGameIcon from '@swf/components/CasinoGameIcon/CasinoGameIcon';
import Counter from '@swf/components/Counter/Counter';
import CurrencySymbol from '@swf/components/CurrencySymbol/CurrencySymbol';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { raceEntity } from '@swf/core/analytics';
import fsbIcon from '@swf/core/assets/casino/free-spins.png';
import InfoIcon from '@swf/core/assets/casino/info.svg';
import ibIcon from '@swf/core/assets/casino/instant-bonus-glow.png';
import { Size } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import TrackingContext from '@swf/core/providers/TrackingContext';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { PrizeType } from '@swf/core/types/CasinoRaces';
import { isCompleted, isRunning } from '@swf/core/utils/casino';
import { CasinoIcon } from '@swf/generated/types/common/Casino';

import RaceDetails from '../RaceDetails/RaceDetails';
import RacePlayButton from '../RacePlayButton/RacePlayButton';
import type { DisplayStatus } from '../RegisteredPlayers/RegisteredPlayers';
import RegisteredPlayers from '../RegisteredPlayers/RegisteredPlayers';

import { TXTMOB_Total_Prizes } from './CasinoRaceCard.messages.json';
import styles from './CasinoRaceCard.scss';

export interface Props {
  race: CasinoRace;
  isModal?: boolean;
  racesLength: number;
  widgetId?: string;
  path?: string;
}

const CasinoRaceCard: React.FC<Props> = ({ race, isModal, racesLength, widgetId, path }) => {
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const {
    prizeDetails,
    endTime,
    status,
    gameInfo,
    startTime,
    numOfPlayers,
    userRaceStanding,
    minBet,
    batchSize,
    feature
  } = race;
  const { total } = prizeDetails;
  const running = isRunning(status);
  const completed = isCompleted(status);
  const registered = !completed && !running && !isNil(userRaceStanding);
  const showStanding = isModal && isAuthenticated && userRaceStanding;
  const showRequirements = racesLength === 1 && (!!minBet || !!batchSize);
  const { modal } = useDispatch<Dispatch>();
  const trackingContext = useContext(TrackingContext);

  const launchRaceInfo = useCallback(() => {
    if (!isModal) {
      modal.replace([{ path: '/casino/race-info', locationState: { widgetId, path } }]);
    }
  }, [isModal, modal, widgetId, path]);

  let statusName: DisplayStatus = 'none';
  switch (true) {
    case running:
      statusName = 'running';
      break;
    case completed:
      statusName = 'completed';
      break;
    case registered:
      statusName = 'registered';
      break;
    default:
      statusName = 'none';
      break;
  }

  const cardStyle = () => {
    switch (true) {
      case isModal:
        return styles.small;
      case racesLength === 1:
        return styles.large;
      case racesLength === 2:
        return styles.medium;
      default:
        return styles.small;
    }
  };

  const prizeDisplay = () => (
    <>
      {prizeDetails.prizeType === PrizeType.IB && <img src={ibIcon} alt="" />}
      {prizeDetails.prizeType === PrizeType.FSB && <img src={fsbIcon} alt="" />}
      <PrizeCurrency type={prizeDetails.prizeType} amount={total} />
    </>
  );

  return (
    <TrackingContext.Provider value={{ ...trackingContext, entities: [raceEntity(race)] }}>
      <Counter>
        {(count) => (
          <div
            className={classNames(
              {
                [styles.itemModal]: isModal,
                [styles.itemListStretch]: !isModal && !isMobile,
                [styles.itemListFixed]: !isModal && isMobile
              },
              racesLength > 1 && styles.many,
              feature && styles.featured,
              cardStyle()
            )}
          >
            <div
              className={classNames(styles.header, isModal && styles.modal)}
              onClick={launchRaceInfo}
            >
              <CasinoGameIcon
                className={isModal ? styles.modalHeaderImg : styles.gameItemHeaderImg}
                disabled
                game={gameInfo}
                icon={CasinoIcon.icnWide}
                race={race}
                tooltip=""
              >
                <RegisteredPlayers status={statusName} numOfPlayers={numOfPlayers} />
              </CasinoGameIcon>
            </div>
            <div
              className={classNames(
                isModal ? styles.gameInfoModal : styles.gameInfoList,
                racesLength === 1 && styles.one,
                racesLength === 2 && styles.two,
                racesLength >= 3 && styles.three
              )}
            >
              <div className={styles.prizeSection}>
                <div className={classNames(styles.gameItemTotal, isModal && styles.modal)}>
                  <Translation {...TXTMOB_Total_Prizes} />
                  {prizeDetails.prizeType === PrizeType.CASH && (
                    <>
                      {' ('}
                      <CurrencySymbol currency={prizeDetails.currency} trimSpace />
                      {')'}
                    </>
                  )}
                </div>
                <div className={classNames(styles.gameItemValue, isModal && styles.modal)}>
                  {prizeDisplay()}
                </div>
              </div>
              <RaceDetails
                minBet={minBet}
                completed={completed}
                running={running}
                endTime={endTime}
                startTime={startTime}
                count={count}
                userRaceStanding={showStanding ? userRaceStanding : undefined}
                isModal={isModal}
                showRequirements={showRequirements}
                featured={racesLength === 1}
              />
              {!completed && (
                <div className={classNames(styles.buttons, isModal && styles.modal)}>
                  <RacePlayButton
                    race={race}
                    widgetId={widgetId}
                    path={path}
                    size={!isModal && !greaterThan(Size.Large) ? 'small' : 'large'}
                  />
                  {!isModal && (
                    <StarsButton
                      variant="text"
                      className={styles.infoIcon}
                      onClick={launchRaceInfo}
                      icon={<InfoIcon />}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Counter>
    </TrackingContext.Provider>
  );
};

export default CasinoRaceCard;
